import { CartItems } from '@/hooks/CartContext';
import { calcPrice } from '@/shared/calculatePrices';
import { PriceProductProps, ProductProps, TaxSubstitutionProps } from '@/types';
import {
    ComboCartItemAllocation,
    ComboRequirementGroup,
    findComboRequirementGroup,
    getComboDiscountParticipatingQuantityForCartItem,
    getGrupoLabel,
    normalizeComboName,
} from './comboCartUtils';
import { isActiveComboPortionFromAllocations } from './comboFlexEligibility';
import {
    ComboCartLinePriceDeps,
    computeCartLineTotalWithComboDiscount,
    computeCheckoutLineStPricing,
    computeStLineTotalLikeStPrice,
    getComboPriceRecordForCartLine,
    getNormalPriceRecordForCartLine,
    lookupPersistedFlexUnitPrice,
    applyFlexPercentToUnitPrice,
    roundMoney,
} from './comboCartTotalsUtils';

type DiscountProductLookup = (params: {
    package: string;
    productId: number;
    quantity: number;
    uid?: string;
}) => { percentual?: number; discountPrice?: Record<string, number> } | undefined;

type FindPriceFn = (
    prices: PriceProductProps[],
    option_uid: string,
    paymentCondition: number
) => PriceProductProps;

/** Mesma regra de preço unitário usada no total do carrinho (flex + condição de pagamento). */
export const resolveCartLineUnitPrice = (
    item: Pick<CartItems, 'sku' | 'option_uid' | 'uid'>,
    prices: PriceProductProps[],
    paymentCondition: number,
    findPrice: FindPriceFn,
    getDiscountProduct: DiscountProductLookup,
    selectedClientScope: Parameters<typeof calcPrice>[0],
    listaTributacao: TaxSubstitutionProps[],
    uidOverride?: string
): number | null => {
    const [packing, qtd] = item.option_uid?.split('_') || [];

    const flexUid = uidOverride ?? item.uid;
    const flexEntry = getDiscountProduct({
        package: packing,
        productId: item.sku,
        quantity: Number(qtd),
        uid: flexUid,
    });
    const percentualFlex = Number(flexEntry?.percentual ?? 0);
    const rawDiscount = lookupPersistedFlexUnitPrice(flexEntry?.discountPrice, paymentCondition);
    const price = findPrice(prices, item.option_uid, paymentCondition);

    let finalprice: number | undefined;

    if (rawDiscount != null) {
        finalprice = rawDiscount;
    } else if (percentualFlex !== 0 && price) {
        const vlrPreco = Number(price.VLR_PRECO);
        if (Number.isFinite(vlrPreco) && listaTributacao?.length) {
            const adjustedRow: PriceProductProps = {
                ...price,
                VLR_PRECO: vlrPreco + (vlrPreco / 100) * percentualFlex,
            };
            const calculated = calcPrice(selectedClientScope, [adjustedRow], listaTributacao);
            finalprice = calculated[0]?.VLR_FINAL;
        }
        if (finalprice === undefined || finalprice === null || Number.isNaN(finalprice)) {
            const base = Number(price.VLR_FINAL ?? price.VLR_PRECO);
            if (Number.isFinite(base)) {
                finalprice = applyFlexPercentToUnitPrice(base, percentualFlex);
            }
        }
    }

    if (finalprice === undefined || finalprice === null || Number.isNaN(finalprice)) {
        finalprice = price?.VLR_FINAL || price?.VLR_PRECO;
    }

    return finalprice ?? null;
};

export interface CheckoutLineComboPricing {
    lineTotal: number;
    unitPrice: number;
    unitPricePerItem: number;
}

export const buildCheckoutLinePriceWithCombo = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid' | 'quantity'>,
    normalUnitPrice: number,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    packQty: number,
    deps: ComboCartLinePriceDeps
): CheckoutLineComboPricing => {
    const quantity = item.quantity || 0;
    const safePackQty = packQty > 0 ? packQty : 1;

    if (!quantity || !normalUnitPrice) {
        return { lineTotal: 0, unitPrice: 0, unitPricePerItem: 0 };
    }

    const lineTotal = roundMoney(
        allocations?.length
            ? computeCartLineTotalWithComboDiscount(
                  item as CartItems,
                  normalUnitPrice,
                  allocations,
                  baseProduct,
                  paymentCondition,
                  deps
              )
            : normalUnitPrice * quantity
    );

    const unitPrice = lineTotal / quantity;
    const unitPricePerItem = unitPrice / safePackQty;

    return { lineTotal, unitPrice, unitPricePerItem };
};

/**
 * Retorna o preço base por item que deve ser integrado no pedido.
 * Para linhas parcialmente atendidas por combo, combina os preços base de
 * cada parcela proporcionalmente à quantidade de embalagens.
 */
export const buildCheckoutIntegrationPriceWithCombo = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid' | 'quantity'>,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps,
    fallbackIntegrationPrice: number
): number => {
    const quantity = item.quantity || 0;
    const discountQty = getComboDiscountParticipatingQuantityForCartItem(item, allocations);

    if (!quantity || discountQty <= 0) return fallbackIntegrationPrice;

    const comboRecord = getComboPriceRecordForCartLine(
        item,
        allocations,
        baseProduct,
        paymentCondition,
        deps
    );
    const comboBasePrice = Number(comboRecord?.VLR_PRECO_CALCULADO);

    if (!Number.isFinite(comboBasePrice) || comboBasePrice <= 0) {
        return fallbackIntegrationPrice;
    }

    const packagingQty = Number(
        comboRecord?.QTD_MULTIPLO_VENDA || baseProduct?.EMBALAGEM?.find((packaging) => {
            const [pack, qty] = item.option_uid?.split('_') || [];
            return (
                packaging.SG_EMBALAGEM === pack &&
                Number(packaging.QTD_EMBALAGEM || packaging.QTD_MULTIPLO_VENDA) === Number(qty)
            );
        })?.QTD_MULTIPLO_VENDA || item.option_uid?.split('_')[1] || 1
    );
    const safePackagingQty = packagingQty > 0 ? packagingQty : 1;
    const comboIntegrationPrice = comboBasePrice / safePackagingQty;

    if (discountQty >= quantity) return comboIntegrationPrice;

    const normalRecord = getNormalPriceRecordForCartLine(item, baseProduct, paymentCondition, deps);
    const normalBasePrice = Number(normalRecord?.VLR_PRECO_CALCULADO);
    const normalIntegrationPrice =
        Number.isFinite(normalBasePrice) && normalBasePrice > 0
            ? normalBasePrice / safePackagingQty
            : fallbackIntegrationPrice;
    const regularQty = quantity - discountQty;

    return (comboIntegrationPrice * discountQty + normalIntegrationPrice * regularQty) / quantity;
};

/** Texto de reason para itens em combo ativo: "COMBO 2 — Grupo 2.0". */
export const buildComboReasonForCartItem = (
    allocations: ComboCartItemAllocation[] | undefined,
    combos: Array<{ id_campanha?: string; grupos_exigencia?: ComboRequirementGroup[] }>
): string => {
    if (!allocations?.length) return '';

    const activeAllocations = allocations.filter((entry) => (entry.allocatedPackaging || 0) > 0);
    if (!activeAllocations.length) return '';

    const labels = new Set<string>();

    activeAllocations.forEach((allocation) => {
        const combo = combos.find((entry) => entry.id_campanha === allocation.id_campanha);
        const comboName =
            allocation.nome_campanha ||
            normalizeComboName(combo || { id_campanha: allocation.id_campanha });
        const grupo = findComboRequirementGroup(combo?.grupos_exigencia, allocation.grupoId);
        const grupoLabel = getGrupoLabel(grupo, allocation.grupoId || 'Grupo');
        labels.add(`${comboName} — ${grupoLabel}`);
    });

    return Array.from(labels).join(' | ');
};

export interface CheckoutDisplayPricing extends CheckoutLineComboPricing {
    originalLineTotal: number;
    hasComboDiscount: boolean;
    stLineTotal: number;
    stPerPackaging: number;
    priceRecordForSt: PriceProductProps | null;
    hasComboStSplit: boolean;
    /** Flex aplicado em cima do preço atual da linha (combo ou tabela), com ST recalculado. */
    hasFlex: boolean;
    percentualFlex: number;
    unitPriceFlex: number;
    unitPricePerItemFlex: number;
    lineTotalFlex: number;
}

export interface CheckoutDisplayPricingContext {
    paymentCode: number;
    findPrice: FindPriceFn;
    getDiscountProduct: DiscountProductLookup;
    selectedClientScope: Parameters<typeof calcPrice>[0];
    selectedClient: Parameters<typeof calcPrice>[0];
    listaTributacao: TaxSubstitutionProps[];
    checkoutComboPriceDeps: ComboCartLinePriceDeps;
    getAllocations?: (uid: string) => ComboCartItemAllocation[] | undefined;
}

const toPriceList = (rawPrecos: ProductProps['PRECO']): PriceProductProps[] => {
    if (!rawPrecos) return [];
    return Array.isArray(rawPrecos) ? rawPrecos : Object.values(rawPrecos);
};

export const buildCheckoutDisplayPricingForEntry = (
    entry: { item: CartItems; product: ProductProps },
    context: CheckoutDisplayPricingContext
): CheckoutDisplayPricing => {
    const { item, product } = entry;
    const [, qtdEmb] = item.option_uid?.split('_') || [];
    const packQty = Number(qtdEmb) || 1;
    const quantity = item.quantity || 0;
    const prices = toPriceList(product.PRECO);

    const allocations = context.getAllocations?.(item.uid);
    // Porção com combo ATIVO ignora o flex (ver ./comboFlexEligibility) — o desconto de combo vale
    // sozinho. O flex persistido continua salvo, só deixa de ser lido aqui enquanto o combo for ativo.
    const isComboActivePortion = isActiveComboPortionFromAllocations(item, allocations);
    const flexLookup: DiscountProductLookup = isComboActivePortion
        ? () => undefined
        : context.getDiscountProduct;

    const normalUnitPrice =
        resolveCartLineUnitPrice(
            item,
            prices,
            context.paymentCode,
            context.findPrice,
            flexLookup,
            context.selectedClientScope,
            context.listaTributacao
        ) ?? 0;

    const comboLinePricing = buildCheckoutLinePriceWithCombo(
        item,
        normalUnitPrice,
        allocations,
        product,
        context.paymentCode,
        packQty,
        context.checkoutComboPriceDeps
    );

    const originalLineTotal = normalUnitPrice * quantity;

    const flexEntry = flexLookup({
        package: item.option_uid?.split('_')[0] || '',
        productId: item.sku,
        quantity: packQty,
        uid: item.uid,
    });
    const percentualFlex = Number(flexEntry?.percentual ?? 0);
    const hasFlex = percentualFlex !== 0 || !!flexEntry?.percentual;

    const stPricing = computeCheckoutLineStPricing(
        item,
        allocations,
        product,
        context.paymentCode,
        context.checkoutComboPriceDeps,
        context.selectedClient,
        context.listaTributacao,
        percentualFlex
    );

    const discountQty = getComboDiscountParticipatingQuantityForCartItem(item, allocations);
    const comboRecord =
        discountQty > 0
            ? getComboPriceRecordForCartLine(
                  item,
                  allocations,
                  product,
                  context.paymentCode,
                  context.checkoutComboPriceDeps
              )
            : null;
    const normalRecord = getNormalPriceRecordForCartLine(
        item,
        product,
        context.paymentCode,
        context.checkoutComboPriceDeps
    );
    const hasComboDiscount = comboLinePricing.lineTotal < originalLineTotal;
    const regularQty = Math.max((item.quantity || 0) - discountQty, 0);
    const hasComboStSplit = discountQty > 0 && regularQty > 0;
    const priceRecordForSt =
        hasComboDiscount && comboRecord ? comboRecord : normalRecord;

    // Mesma regra do payload: sem combo usa o valor persistido; com combo recalcula o ST sobre o registro certo.
    let unitPriceFlex = 0;
    if (hasFlex) {
        const rawFlexDiscount = lookupPersistedFlexUnitPrice(flexEntry?.discountPrice, context.paymentCode);
        const hasPersistedFlexDiscount = rawFlexDiscount !== undefined && rawFlexDiscount !== null;

        if (!allocations?.length && hasPersistedFlexDiscount) {
            unitPriceFlex = Number(rawFlexDiscount);
        } else {
            const flexBaseRecord = allocations?.length ? comboRecord : normalRecord;
            const precoCalculadoFlexUnit = Number(flexBaseRecord?.VLR_PRECO_CALCULADO) * (1 + percentualFlex / 100);
            const stLineTotalFlex = computeStLineTotalLikeStPrice(
                flexBaseRecord,
                quantity,
                context.selectedClient,
                context.listaTributacao,
                percentualFlex
            );
            unitPriceFlex =
                quantity > 0 && Number.isFinite(precoCalculadoFlexUnit)
                    ? precoCalculadoFlexUnit + stLineTotalFlex / quantity
                    : 0;
        }
    }
    const safePackQty = packQty > 0 ? packQty : 1;
    const unitPricePerItemFlex = unitPriceFlex / safePackQty;
    const lineTotalFlex = roundMoney(unitPriceFlex * quantity);

    return {
        ...comboLinePricing,
        originalLineTotal,
        hasComboDiscount,
        stLineTotal: stPricing.stLineTotal,
        stPerPackaging: stPricing.stPerPackaging,
        priceRecordForSt,
        hasComboStSplit,
        hasFlex,
        percentualFlex,
        unitPriceFlex,
        unitPricePerItemFlex,
        lineTotalFlex,
    };
};

export const enrichCheckoutProductListWithPricing = <T extends { item: CartItems; product: ProductProps }>(
    productList: T[],
    context: CheckoutDisplayPricingContext
): Array<T & { checkoutPricing: CheckoutDisplayPricing }> =>
    productList.map((entry) => ({
        ...entry,
        checkoutPricing: buildCheckoutDisplayPricingForEntry(entry, context),
    }));

export const sumCheckoutItemLineTotals = (items: Array<{ price?: string | number }>): number =>
    roundMoney(items.reduce((sum, item) => sum + Number(item.price || 0), 0));

export const checkoutTotalMatchesCart = (
    itemsTotal: number,
    cartTotal: number,
    tolerance = 0.02
): boolean => Math.abs(itemsTotal - cartTotal) <= tolerance;
